import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { formatPrice } from '../../utils/currency';

const Dashboard = () => {
  const [stats, setStats] = useState({
    stores: { total: 0, active: 0, inactive: 0 },
    products: { total: 0, active: 0, inactive: 0 },
    categories: { total: 0, with_products: 0, empty: 0 },
    orders: { total: 0 },
    recent_orders: [],
    top_products: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/dashboard');
      
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        // Fallback to mock data if API fails
        setStats({
          stores: { total: 12, active: 8, inactive: 4 },
          products: { total: 156, active: 120, inactive: 36 },
          categories: { total: 25, with_products: 20, empty: 5 },
          orders: { total: 89 },
          recent_orders: [
            { id: 1, order_number: 'ORD-2025-000001', customer_name: 'Tech', store_name: 'Tech Store', total_amount: '299.99', status: 'completed', created_at: '2024-01-15' },
            { id: 2, order_number: 'ORD-2025-000002', customer_name: 'Fashion', store_name: 'Fashion Hub', total_amount: '149.50', status: 'pending', created_at: '2024-01-14' },
            { id: 3, order_number: 'ORD-2025-000003', customer_name: 'Home', store_name: 'Home Goods', total_amount: '89.99', status: 'processing', created_at: '2024-01-13' }
          ],
          top_products: [
            { product_id: 1, product_name: 'iPhone 15 Pro', price: '500.00', discount_percentage: 10, final_price: 450, total_sold: '45' },
            { product_id: 2, product_name: 'Nike Air Max', price: '200.00', discount_percentage: 0, final_price: 200, total_sold: '32' },
            { product_id: 3, product_name: 'Coffee Maker', price: '150.00', discount_percentage: 5, final_price: 142.5, total_sold: '28' }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Fallback to mock data
      setStats({
        stores: { total: 12, active: 8, inactive: 4 },
        products: { total: 156, active: 120, inactive: 36 },
        categories: { total: 25, with_products: 20, empty: 5 },
        orders: { total: 89 },
        recent_orders: [
          { id: 1, order_number: 'ORD-2025-000001', customer_name: 'Tech', store_name: 'Tech Store', total_amount: '299.99', status: 'pending', created_at: '2024-01-15' },
          { id: 2, order_number: 'ORD-2025-000002', customer_name: 'Fashion', store_name: 'Fashion Hub', total_amount: '149.50', status: 'pending', created_at: '2024-01-14' },
          { id: 3, order_number: 'ORD-2025-000003', customer_name: 'Home', store_name: 'Home Goods', total_amount: '89.99', status: 'pending', created_at: '2024-01-13' }
        ],
        top_products: [
          { product_id: 1, product_name: 'iPhone 15 Pro', price: '500.00', discount_percentage: 10, final_price: 450, total_sold: '45' },
          { product_id: 2, product_name: 'Nike Air Max', price: '200.00', discount_percentage: 0, final_price: 200, total_sold: '32' },
          { product_id: 3, product_name: 'Coffee Maker', price: '150.00', discount_percentage: 5, final_price: 142.5, total_sold: '28' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const quickActions = [
    {
      title: 'Add New Store',
      description: 'Create a new store',
      icon: '🏪',
      href: '/admin/stores/create',
      color: 'bg-blue-500 hover:bg-blue-600',
    },
    {
      title: 'Manage Stores',
      description: 'View and edit existing stores',
      icon: '📋',
      href: '/admin/stores',
      color: 'bg-green-500 hover:bg-green-600',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to your admin dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <span className="text-2xl">🏪</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Stores</p>
              <p className="text-2xl font-bold text-gray-900">{stats.stores.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Stores</p>
              <p className="text-2xl font-bold text-gray-900">{stats.stores.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <span className="text-2xl">📦</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{stats.products.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <span className="text-2xl">📋</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.orders.total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <span className="text-2xl">📂</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Categories</p>
              <p className="text-2xl font-bold text-gray-900">{stats.categories.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-teal-100 rounded-lg">
              <span className="text-2xl">📦</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Categories with Products</p>
              <p className="text-2xl font-bold text-gray-900">{stats.categories.with_products}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <span className="text-2xl">⚠️</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Empty Categories</p>
              <p className="text-2xl font-bold text-gray-900">{stats.categories.empty}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.title}
              to={action.href}
              className={`${action.color} text-white rounded-lg p-6 transition-colors duration-200 hover:shadow-md`}
            >
              <div className="flex items-center">
                <span className="text-3xl mr-4">{action.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold">{action.title}</h3>
                  <p className="text-blue-100">{action.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
        </div>
        
        {stats.recent_orders.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No recent orders</p>
        ) : (
          <div className="space-y-4">
            {stats.recent_orders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600">📋</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{order.store_name}</p>
                    <p className="text-sm text-gray-500">
                      {order.order_number} • {order.customer_name} • {formatDate(order.created_at)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{formatPrice(order.total_amount, 'EGP')}</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                    {order.status === 'pending' ? 'created' : order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top Products */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Top Products</h2>
        </div>
        
        {stats.top_products.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No top products data</p>
        ) : (
          <div className="space-y-4">
            {stats.top_products.map((product, index) => (
              <div key={product.product_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600 font-bold">#{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{product.product_name}</p>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <span>{formatPrice(product.price, 'EGP')}</span>
                      {parseFloat(product.discount_percentage) > 0 && (
                        <>
                          <span className="text-green-600">→</span>
                          <span className="text-green-600 font-medium">{formatPrice(product.final_price, 'EGP')}</span>
                          <span className="text-red-600">({product.discount_percentage}% OFF)</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{product.total_sold} sold</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 